import { Fragment, useState } from "react";
import useSWR from "swr";

import { api, AdminUser, AdminTier, AdminUsage, CuratedFundEntry, CuratedFundList } from "../api";
import { PageHeader } from "../ui";
import { RequestLog } from "./Usage";

const fetcher = <T,>(path: string) => api.get<T>(path);

type Tab = "users" | "tiers" | "curated-funds" | "usage";

const TABS: { key: Tab; label: string }[] = [
  { key: "users", label: "Users" },
  { key: "tiers", label: "Tiers" },
  { key: "curated-funds", label: "Curated Funds" },
  { key: "usage", label: "Usage" },
];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Confirm modal ────────────────────────────────────────────

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
  variant: "danger" | "warning";
  onConfirm: () => Promise<void>;
}

function ConfirmModal({ state, onClose }: { state: ConfirmState; onClose: () => void }) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await state.onConfirm();
    } finally {
      setBusy(false);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[400px] mx-4 flex flex-col gap-3 p-6 rounded-md bg-card border border-border animate-scale-in">
        <h3 className="text-sm font-semibold text-main">{state.title}</h3>
        <p className="text-xs text-muted">{state.message}</p>
        <div className="flex gap-2 mt-1">
          {state.variant === "danger" ? (
            <button className="btn-danger" disabled={busy} onClick={handleConfirm}>
              {busy ? "Processing..." : state.confirmLabel}
            </button>
          ) : (
            <button className="btn-primary" disabled={busy} onClick={handleConfirm}>
              {busy ? "Processing..." : state.confirmLabel}
            </button>
          )}
          <button className="btn-default" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin page ──────────────────────────────────────────────

export function Admin() {
  const [tab, setTab] = useState<Tab>("users");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [usageUser, setUsageUser] = useState("");
  const showUsage = (userId: string) => { setUsageUser(userId); setTab("usage"); };

  return (
    <section>
      <PageHeader title="Administration" description="Manage users, tiers, the curated fund list, and API usage." />

      <div role="tablist" className="flex gap-6 mb-8 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`-mb-px pb-3 border-b-2 text-[15px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
              tab === t.key ? "border-accent text-accent" : "border-transparent text-muted hover:text-main"
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersSection onConfirm={setConfirm} onUsage={showUsage} />}
      {tab === "tiers" && <TiersSection />}
      {tab === "curated-funds" && <CuratedFundsSection onConfirm={setConfirm} />}
      {tab === "usage" && <UsageSection userId={usageUser} onUserChange={setUsageUser} />}

      {confirm && <ConfirmModal state={confirm} onClose={() => setConfirm(null)} />}
    </section>
  );
}

// ── Users section ───────────────────────────────────────────

type StatusFilter = "all" | "active" | "suspended" | "unverified" | "admins";

const CONTROL = "px-2 py-1.5 rounded-md text-xs font-sans outline-none bg-input border border-input-border text-main focus:border-accent";
const TH = "px-3 py-2";

function matchesStatus(u: AdminUser, s: StatusFilter) {
  switch (s) {
    case "active": return u.is_active;
    case "suspended": return !u.is_active;
    case "unverified": return !u.email_verified;
    case "admins": return u.roles.includes("admin");
    default: return true;
  }
}

// ponytail: client-side search/filter over the full list; add server-side search + paging once /admin/users passes a few thousand rows
function UsersSection({ onConfirm, onUsage }: { onConfirm: (s: ConfirmState) => void; onUsage: (id: string) => void }) {
  const { data: users, error: usersErr, mutate } = useSWR<AdminUser[]>("/admin/users", fetcher);
  const { data: tiers } = useSWR<AdminTier[]>("/admin/tiers", fetcher);
  const { data: allFeatures } = useSWR<string[]>("/admin/features", fetcher);
  const [showCreate, setShowCreate] = useState(false);
  const [query, setQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  if (usersErr) return <p className="text-sm text-neg">Failed to load users.</p>;

  const q = query.trim().toLowerCase();
  const visible = (users ?? []).filter(
    (u) =>
      (!q || u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q)) &&
      (!tierFilter || (u.tier_name ?? "free") === tierFilter) &&
      matchesStatus(u, statusFilter),
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">Users</h2>
        {!showCreate && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            New user
          </button>
        )}
      </div>

      {showCreate && (
        <CreateUserForm
          allFeatures={allFeatures ?? []}
          onCreated={() => { setShowCreate(false); mutate(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <input
          type="search"
          placeholder="Search email or name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={`${CONTROL} w-64 max-sm:w-full`}
        />
        <select className={CONTROL} value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
          <option value="">All tiers</option>
          {(tiers ?? []).map((t) => (
            <option key={t.name} value={t.name}>{t.display_name}</option>
          ))}
        </select>
        <select className={CONTROL} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="unverified">Unverified</option>
          <option value="admins">Admins</option>
        </select>
        {users && (
          <span className="text-xs text-muted ml-auto tabular-nums">
            {visible.length === users.length ? `${users.length} users` : `${visible.length} of ${users.length} users`}
          </span>
        )}
      </div>

      {!users && <p className="text-sm text-muted">Loading...</p>}
      {users && users.length === 0 && <p className="text-sm text-muted">No users found.</p>}
      {users && users.length > 0 && visible.length === 0 && <p className="text-sm text-muted">No users match.</p>}
      {visible.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th className={TH}>Email</th>
                <th className={`${TH} max-sm:hidden`}>Tier</th>
                <th className={TH}>Features</th>
                <th className={`${TH} max-sm:hidden`}>Created</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  open={openId === u.id}
                  onToggle={() => setOpenId(openId === u.id ? null : u.id)}
                  tiers={tiers ?? []}
                  allFeatures={allFeatures ?? []}
                  onChange={mutate}
                  onConfirm={onConfirm}
                  onUsage={onUsage}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Create user form ────────────────────────────────────────

function CreateUserForm({
  allFeatures,
  onCreated,
  onCancel,
}: {
  allFeatures: string[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set(["nh.programs.read"]));
  const [isAdmin, setIsAdmin] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggleFeature = (key: string) => {
    setSelectedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/admin/users", {
        email: email.trim(),
        feature_keys: [...selectedFeatures],
        is_admin: isAdmin,
        send_invite: sendInvite,
      });
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3.5 p-6 rounded-md mb-6 bg-card border border-border" onSubmit={submit}>
      <h3 className="text-sm font-semibold text-main">New user</h3>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">Email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          className="px-3 py-2 rounded-md text-sm font-sans outline-none transition-colors bg-input border border-input-border text-main focus:border-accent"
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">Features</span>
        <div className="flex gap-1.5 flex-wrap">
          {allFeatures.map((f) => (
            <button
              key={f}
              type="button"
              className={`px-3 py-1.5 rounded-md text-xs font-medium font-sans cursor-pointer transition-colors border ${
                selectedFeatures.has(f)
                  ? "bg-accent border-accent text-white"
                  : "bg-input border-border text-muted hover:border-muted hover:text-main"
              }`}
              onClick={() => toggleFeature(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-xs text-muted cursor-pointer">
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
          Admin role
        </label>
        <label className="flex items-center gap-2 text-xs text-muted cursor-pointer">
          <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
          Send invite email
        </label>
      </div>

      {error && <p className="text-[13px] text-neg">{error}</p>}

      <div className="flex gap-2 mt-1">
        <button type="submit" className="btn-primary" disabled={busy || !email.trim()}>
          {busy ? "Creating..." : "Create"}
        </button>
        <button type="button" className="btn-default" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── User row ────────────────────────────────────────────────

function Badge({ children, tone }: { children: React.ReactNode; tone: "accent" | "neg" | "warning" }) {
  const cls = { accent: "bg-accent/10 text-accent", neg: "bg-neg-bg text-neg", warning: "bg-warning-bg text-warning" }[tone];
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;
}

function UserRow({
  user,
  open,
  onToggle,
  tiers,
  allFeatures,
  onChange,
  onConfirm,
  onUsage,
}: {
  user: AdminUser;
  open: boolean;
  onToggle: () => void;
  tiers: AdminTier[];
  allFeatures: string[];
  onChange: () => void;
  onConfirm: (s: ConfirmState) => void;
  onUsage: (id: string) => void;
}) {
  const shortFeatures = user.features.map((f) => f.replace(/^nh\./, ""));
  const ourEnd = user.our_tier_expires_at ? new Date(user.our_tier_expires_at) : null;
  const ourEnded = ourEnd !== null && ourEnd < new Date();
  const ourEndLabel = ourEnd?.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  return (
    <Fragment>
      <tr
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className={`cursor-pointer hover:bg-stat focus:bg-stat outline-none ${open ? "bg-stat" : ""} ${
          !user.is_active ? "opacity-50" : ""
        }`}
      >
        <td className="px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <span aria-hidden className="inline-block w-3 text-muted">{open ? "▾" : "▸"}</span>
            <span className="font-medium text-main truncate">{user.email}</span>
            {user.roles.includes("admin") && <Badge tone="accent">admin</Badge>}
            {!user.is_active && <Badge tone="neg">Suspended</Badge>}
            {!user.email_verified && <Badge tone="warning">Unverified</Badge>}
          </div>
        </td>
        <td className="px-3 py-2 text-muted max-sm:hidden">
          <div className="flex items-center gap-1.5 flex-wrap">
            {user.tier_name ?? "free"}
            {user.our_tier_name && (
              <Badge tone={ourEnded ? "warning" : "accent"}>
                ours: {user.our_tier_name}
                {ourEndLabel && ` · ${ourEnded ? "ended" : "ends"} ${ourEndLabel}`}
              </Badge>
            )}
            {user.nh_sync_due && <Badge tone="warning">NH not told yet</Badge>}
          </div>
        </td>
        <td className="px-3 py-2 text-muted whitespace-nowrap">
          {shortFeatures.length === 0 && <span className="opacity-60">none</span>}
          {shortFeatures.slice(0, 2).join(", ")}
          {shortFeatures.length > 2 && <span> +{shortFeatures.length - 2}</span>}
        </td>
        <td className="px-3 py-2 text-muted whitespace-nowrap max-sm:hidden">{formatDate(user.created_at)}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="p-0 bg-stat">
            <UserDetail
              user={user}
              tiers={tiers}
              allFeatures={allFeatures}
              onChange={onChange}
              onConfirm={onConfirm}
              onUsage={onUsage}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function UserDetail({
  user,
  tiers,
  allFeatures,
  onChange,
  onConfirm,
  onUsage,
}: {
  user: AdminUser;
  tiers: AdminTier[];
  allFeatures: string[];
  onChange: () => void;
  onConfirm: (s: ConfirmState) => void;
  onUsage: (id: string) => void;
}) {

  const [error, setError] = useState<string | null>(null);
  const [grantKey, setGrantKey] = useState("");
  // The plan we give the user; their NilssonHedge plan is kept alongside, and the API applies
  // whichever wins (ours when it's higher, or lower until they buy or renew on NilssonHedge).
  const ours = user.our_tier_name ?? "";
  const [tierSelect, setTierSelect] = useState(ours);
  const today = new Date().toISOString().slice(0, 10);
  const [purchased, setPurchased] = useState(today);

  const grantableFeatures = allFeatures.filter((f) => !user.features.includes(f));

  const grantFeature = async () => {
    if (!grantKey) return;
    setError(null);
    try {
      await api.post(`/admin/users/${user.id}/features/grant`, { feature_key: grantKey });
      setGrantKey("");
      onChange();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to grant feature");
    }
  };

  const revokeFeature = (key: string) => {
    onConfirm({
      title: "Revoke feature",
      message: `Remove "${key}" from ${user.email}?`,
      confirmLabel: "Revoke",
      variant: "warning",
      onConfirm: async () => {
        setError(null);
        try {
          await api.post(`/admin/users/${user.id}/features/revoke`, { feature_key: key });
          onChange();
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : "Failed to revoke feature");
        }
      },
    });
  };

  const assignTier = async () => {
    if (!tierSelect) return;
    setError(null);
    try {
      await api.post(`/admin/users/${user.id}/tier`, { tier_name: tierSelect, purchased_on: purchased });
      onChange();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to assign tier");
    }
  };

  const removeTier = () => {
    onConfirm({
      title: "Remove our plan",
      message: `End the plan we gave ${user.email}? They go back to their NilssonHedge plan, or Free if they have none.`,
      confirmLabel: "Remove",
      variant: "warning",
      onConfirm: async () => {
        setError(null);
        try {
          await api.delete(`/admin/users/${user.id}/tier`);
          setTierSelect("");
          onChange();
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : "Failed to remove tier");
        }
      },
    });
  };

  return (
    <div className="flex flex-col gap-3 px-4 py-3 animate-fade-in">
      {/* Features */}
      <div className="flex items-start gap-3">
        <span className="w-16 shrink-0 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted">Features</span>
        <div className="flex flex-wrap items-center gap-1.5">
          {user.features.map((f) => (
            <span
              key={f}
              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-input border border-border text-muted"
            >
              {f}
              <button
                className="ml-0.5 text-neg hover:text-main cursor-pointer"
                onClick={() => revokeFeature(f)}
                title={`Revoke ${f}`}
              >
                &times;
              </button>
            </span>
          ))}
          {grantableFeatures.length > 0 && (
            <div className="flex items-center gap-1">
              <select
                className="px-2 py-1 rounded-md text-[11px] font-sans outline-none bg-input border border-input-border text-main"
                value={grantKey}
                onChange={(e) => setGrantKey(e.target.value)}
              >
                <option value="">+ feature</option>
                {grantableFeatures.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              {grantKey && (
                <button className="btn-sm btn-default" onClick={grantFeature}>Grant</button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tier */}
      <div className="flex items-center gap-3">
        <span className="w-16 shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted">Our plan</span>
        <select
          className="px-2 py-1 rounded-md text-[11px] font-sans outline-none bg-input border border-input-border text-main"
          value={tierSelect}
          onChange={(e) => setTierSelect(e.target.value)}
        >
          <option value="">None (NilssonHedge's applies)</option>
          {tiers.map((t) => (
            <option key={t.name} value={t.name}>{t.display_name}</option>
          ))}
        </select>
        {tierSelect && (
          <>
            {/* Valid one year from the purchase date. */}
            <input
              type="date"
              aria-label="Purchase date"
              className="px-2 py-1 rounded-md text-[11px] font-sans outline-none bg-input border border-input-border text-main"
              value={purchased}
              max={today}
              onChange={(e) => setPurchased(e.target.value)}
            />
            <button className="btn-sm btn-primary" onClick={assignTier}>
              {tierSelect === ours ? "Renew" : "Assign"}
            </button>
          </>
        )}
        {!tierSelect && ours && (
          <button className="btn-sm btn-primary" onClick={removeTier}>Remove</button>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <button className="btn-sm btn-default" onClick={() => onUsage(user.id)}>Usage</button>
        <span className="ml-auto" />
        {user.is_active ? (
          <button className="btn-sm btn-danger" onClick={() => {
            onConfirm({
              title: "Suspend account",
              message: `Suspend ${user.email}? This will deactivate their account and revoke all API tokens.`,
              confirmLabel: "Suspend",
              variant: "danger",
              onConfirm: async () => {
                setError(null);
                try {
                  await api.post(`/admin/users/${user.id}/suspend`);
                  onChange();
                } catch (err: unknown) {
                  setError(err instanceof Error ? err.message : "Failed to suspend user");
                }
              },
            });
          }}>
            Suspend
          </button>
        ) : (
          <button className="btn-sm btn-primary" onClick={async () => {
            setError(null);
            try {
              await api.post(`/admin/users/${user.id}/reactivate`);
              onChange();
            } catch (err: unknown) {
              setError(err instanceof Error ? err.message : "Failed to reactivate user");
            }
          }}>
            Reactivate
          </button>
        )}
      </div>

      {error && <p className="text-[13px] text-neg">{error}</p>}
    </div>
  );
}

// ── Tiers section ───────────────────────────────────────────

function TiersSection() {
  const { data: tiers, error, mutate } = useSWR<AdminTier[]>("/admin/tiers", fetcher);

  if (error) return <p className="text-sm text-neg">Failed to load tiers.</p>;
  if (!tiers) return <p className="text-sm text-muted">Loading...</p>;

  return (
    <div>
      <h2 className="text-lg font-semibold text-main mb-4">Tiers</h2>
      <div className="flex flex-col gap-3">
        {tiers.map((t) => (
          <TierCard key={t.name} tier={t} onChange={mutate} />
        ))}
      </div>
    </div>
  );
}

const FUND_SCOPE_OPTIONS = ["curated", "active_only", "all"];

function TierCard({ tier, onChange }: { tier: AdminTier; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...tier });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setDraft({ ...tier });
    setEditing(true);
    setError(null);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    setError(null);
    setBusy(true);
    const updates: Record<string, unknown> = {};
    for (const key of Object.keys(draft) as (keyof AdminTier)[]) {
      if (key === "id" || key === "name") continue;
      if (draft[key] !== tier[key]) updates[key] = draft[key];
    }
    if (Object.keys(updates).length === 0) {
      setEditing(false);
      setBusy(false);
      return;
    }
    try {
      await api.patch(`/admin/tiers/${tier.name}`, updates);
      onChange();
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update tier");
    } finally {
      setBusy(false);
    }
  };

  const setField = <K extends keyof AdminTier>(key: K, value: AdminTier[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  if (!editing) {
    return (
      <div className="p-4 rounded-md bg-stat border border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-main">{tier.display_name}</span>
            <span className="text-[11px] text-muted font-mono">{tier.name}</span>
            {!tier.is_active && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-neg-bg text-neg">Inactive</span>
            )}
          </div>
          <button className="btn-sm btn-default" onClick={startEdit}>Edit</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-[11px]">
          <div><span className="text-muted">Rate (min/hr/day):</span> <span className="text-main">{tier.requests_per_minute}/{tier.requests_per_hour}/{tier.requests_per_day}</span></div>
          <div><span className="text-muted">Scope:</span> <span className="text-main">{tier.fund_scope}</span></div>
          <div><span className="text-muted">History:</span> <span className="text-main">{tier.history_years ?? "unlimited"} yrs</span></div>
          <div><span className="text-muted">Fund cap:</span> <span className="text-main">{tier.max_funds ?? "none"}</span></div>
          <div><span className="text-muted">Page size:</span> <span className="text-main">{tier.max_results_per_page}</span></div>
          <div><span className="text-muted">Bulk export:</span> <span className="text-main">{tier.can_bulk_export ? `yes (${tier.bulk_export_limit})` : "no"}</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-md bg-card border border-accent">
      <h4 className="text-[13px] font-medium text-main mb-3">Editing: {tier.display_name}</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px]">
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Display name</span>
          <input className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.display_name} onChange={(e) => setField("display_name", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Requests/min</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.requests_per_minute} onChange={(e) => setField("requests_per_minute", +e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Requests/hr</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.requests_per_hour} onChange={(e) => setField("requests_per_hour", +e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Requests/day</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.requests_per_day} onChange={(e) => setField("requests_per_day", +e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Fund scope</span>
          <select className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none" value={draft.fund_scope} onChange={(e) => setField("fund_scope", e.target.value)}>
            {FUND_SCOPE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">History (years)</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.history_years ?? ""} onChange={(e) => setField("history_years", e.target.value ? +e.target.value : null)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Fund cap (blank = none)</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.max_funds ?? ""} onChange={(e) => setField("max_funds", e.target.value ? +e.target.value : null)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Max page size</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.max_results_per_page} onChange={(e) => setField("max_results_per_page", +e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-muted uppercase tracking-wide">Bulk export limit</span>
          <input type="number" min={1} className="px-2 py-1 rounded-md text-xs bg-input border border-input-border text-main outline-none focus:border-accent" value={draft.bulk_export_limit} onChange={(e) => setField("bulk_export_limit", +e.target.value)} />
        </label>
        <div className="flex flex-col gap-2 justify-end">
          <label className="flex items-center gap-2 text-muted cursor-pointer">
            <input type="checkbox" checked={draft.can_bulk_export} onChange={(e) => setField("can_bulk_export", e.target.checked)} />
            Bulk export
          </label>
          <label className="flex items-center gap-2 text-muted cursor-pointer">
            <input type="checkbox" checked={draft.is_active} onChange={(e) => setField("is_active", e.target.checked)} />
            Active
          </label>
        </div>
      </div>
      {error && <p className="text-[13px] text-neg mt-2">{error}</p>}
      <div className="flex gap-2 mt-3">
        <button className="btn-primary" disabled={busy} onClick={save}>
          {busy ? "Saving..." : "Save"}
        </button>
        <button className="btn-default" onClick={cancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Curated funds section ───────────────────────────────────

function CuratedFundsSection({ onConfirm }: { onConfirm: (s: ConfirmState) => void }) {
  const PAGE_SIZE = 100;
  const [offset, setOffset] = useState(0);
  const { data, error, mutate } = useSWR<CuratedFundList>(
    `/admin/curated-funds?limit=${PAGE_SIZE}&offset=${offset}`,
    fetcher,
  );
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (error) return <p className="text-sm text-neg">Failed to load curated funds.</p>;

  const toggleSelect = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const fundKey = (f: CuratedFundEntry) => `${f.manager_id},${f.program_id}`;

  const removeSelected = () => {
    const entries = [...selected].map((k) => {
      const [m, p] = k.split(",");
      return { manager_id: +m, program_id: +p };
    });
    onConfirm({
      title: "Remove curated funds",
      message: `Remove ${entries.length} fund${entries.length !== 1 ? "s" : ""} from the whitelist?`,
      confirmLabel: "Remove",
      variant: "danger",
      onConfirm: async () => {
        await api.delete("/admin/curated-funds", entries);
        setSelected(new Set());
        mutate();
      },
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">
          Curated funds {data ? <span className="font-normal text-muted">({data.total})</span> : null}
        </h2>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <button className="btn-sm btn-danger" onClick={removeSelected}>
              Remove {selected.size}
            </button>
          )}
          {!showAdd && (
            <button className="btn-primary" onClick={() => setShowAdd(true)}>Add funds</button>
          )}
        </div>
      </div>

      {showAdd && (
        <AddFundsForm
          onAdded={() => { setShowAdd(false); mutate(); }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {!data && <p className="text-sm text-muted">Loading...</p>}
      {data && data.items.length === 0 && <p className="text-sm text-muted">No curated funds.</p>}
      {data && data.items.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-8 px-2 py-2"></th>
                  <th className="px-3 py-2">Manager ID</th>
                  <th className="px-3 py-2">Program ID</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((f) => {
                  const k = fundKey(f);
                  return (
                    <tr key={k} className="hover:bg-stat">
                      <td className="px-2 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={selected.has(k)}
                          onChange={() => toggleSelect(k)}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-main font-mono">{f.manager_id}</td>
                      <td className="px-3 py-1.5 text-main font-mono">{f.program_id}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-muted">
              Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
            </p>
            <div className="flex gap-2">
              <button
                className="btn-sm btn-default"
                disabled={offset === 0}
                onClick={() => { setOffset(Math.max(0, offset - PAGE_SIZE)); setSelected(new Set()); }}
              >
                Previous
              </button>
              <button
                className="btn-sm btn-default"
                disabled={offset + PAGE_SIZE >= data.total}
                onClick={() => { setOffset(offset + PAGE_SIZE); setSelected(new Set()); }}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Add funds form ──────────────────────────────────────────

function AddFundsForm({ onAdded, onCancel }: { onAdded: () => void; onCancel: () => void }) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const entries: CuratedFundEntry[] = [];
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const [m, p] = trimmed.split(",").map((s) => s.trim());
      const mid = parseInt(m, 10);
      const pid = parseInt(p, 10);
      if (isNaN(mid) || isNaN(pid)) {
        setError(`Invalid line: "${trimmed}" — expected "manager_id,program_id"`);
        return;
      }
      entries.push({ manager_id: mid, program_id: pid });
    }
    if (entries.length === 0) {
      setError("No entries to add.");
      return;
    }
    if (entries.length > 500) {
      setError("Max 500 entries per request.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/curated-funds", entries);
      onAdded();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add funds");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3.5 p-6 rounded-md mb-6 bg-card border border-border" onSubmit={submit}>
      <h3 className="text-sm font-semibold text-main">Add curated funds</h3>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
          One "manager_id,program_id" per line
        </span>
        <textarea
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"1001,1\n1001,2\n1002,1"}
          className="px-3 py-2 rounded-md text-sm font-mono outline-none transition-colors bg-input border border-input-border text-main focus:border-accent resize-y"
        />
      </label>
      {error && <p className="text-[13px] text-neg">{error}</p>}
      <div className="flex gap-2 mt-1">
        <button type="submit" className="btn-primary" disabled={busy || !text.trim()}>
          {busy ? "Adding..." : "Add"}
        </button>
        <button type="button" className="btn-default" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── Usage section ───────────────────────────────────────────

const DAY_OPTIONS = [7, 30, 90];

function UsageTable({ title, headers, rows }: { title: string; headers: string[]; rows: React.ReactNode[][] }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-main mb-2">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-xs text-muted">No data.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-3 py-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, i) => (
                <tr key={i} className="hover:bg-stat">
                  {cells.map((c, j) => (
                    <td key={j} className="px-3 py-1.5 text-main whitespace-nowrap">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsageSection({ userId, onUserChange }: { userId: string; onUserChange: (id: string) => void }) {
  const [days, setDays] = useState(30);
  const { data: users } = useSWR<AdminUser[]>("/admin/users", fetcher);
  const query = `/admin/usage?days=${days}${userId ? `&user_id=${userId}` : ""}`;
  const { data, error } = useSWR<AdminUsage>(query, fetcher);

  if (error) return <p className="text-sm text-neg">Failed to load usage.</p>;

  const maxDay = data ? Math.max(1, ...data.by_day.map((d) => d.count)) : 1;
  const errorRate = data && data.total ? ((100 * data.error_count) / data.total).toFixed(1) : "0.0";
  const fmtTime = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              className={`px-3 py-1.5 rounded-md text-xs font-medium font-sans cursor-pointer transition-colors border ${
                days === d ? "bg-accent border-accent text-white" : "bg-input border-border text-muted hover:border-muted hover:text-main"
              }`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
        <select
          className="px-2 py-1.5 rounded-md text-xs font-sans outline-none bg-input border border-input-border text-main"
          value={userId}
          onChange={(e) => onUserChange(e.target.value)}
        >
          <option value="">All users</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.email}</option>
          ))}
        </select>
      </div>

      {!data && <p className="text-sm text-muted">Loading...</p>}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
            {[
              ["Requests", data.total.toLocaleString()],
              ["Unique users", data.unique_users.toLocaleString()],
              ["Error rate", `${errorRate}%`],
            ].map(([label, value]) => (
              <div key={label} className="p-4 rounded-md bg-stat border border-border">
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted">{label}</div>
                <div className="text-xl font-semibold text-main mt-1">{value}</div>
              </div>
            ))}
          </div>

          <UsageTable
            title="By day"
            headers={["Day", "Requests", ""]}
            rows={data.by_day.map((d) => [
              d.day,
              d.count.toLocaleString(),
              <div className="h-2 rounded bg-accent min-w-[2px]" style={{ width: `${(100 * d.count) / maxDay}%` }} />,
            ])}
          />

          {!userId && (
            <UsageTable
              title="By user"
              headers={["User", "Requests", "Last seen"]}
              rows={data.by_user.map((u) => [
                u.user_id ? (
                  <button className="text-accent hover:underline cursor-pointer" onClick={() => onUserChange(u.user_id!)}>
                    {u.email ?? u.user_id}
                  </button>
                ) : (
                  <span className="text-muted">unauthenticated</span>
                ),
                u.count.toLocaleString(),
                fmtTime(u.last_seen),
              ])}
            />
          )}

          {userId && (
            <UsageTable
              title="By token"
              headers={["Token", "Requests", "Last seen"]}
              rows={data.by_token.map((t) => [t.name ?? t.token_id, t.count.toLocaleString(), fmtTime(t.last_seen)])}
            />
          )}

          <UsageTable
            title="By route"
            headers={["Method", "Path", "Requests", "Avg ms", "Errors"]}
            rows={data.by_route.map((r) => [
              <span className="font-mono">{r.method}</span>,
              <span className="font-mono">{r.path}</span>,
              r.count.toLocaleString(),
              r.avg_ms,
              r.error_count,
            ])}
          />

          <UsageTable
            title="By status"
            headers={["Status", "Requests"]}
            rows={data.by_status.map((st) => [st.status, st.count.toLocaleString()])}
          />

          <RequestLog
            key={userId}
            source={`/admin/usage/requests${userId ? `?user_id=${userId}` : ""}`}
            showUser={!userId}
          />
        </>
      )}
    </div>
  );
}
